/*
 * Copyright (c) 2026 GS Elektromedizinische Geraete G. Stemple GmbH, Germany - all rights reserved.
 * <p>
 * DO NOT ALTER OR REMOVE COPYRIGHT NOTICES OR THIS FILE HEADER.
 */

import { useContext } from 'react';
import DiviFieldCard from '../../../customs/DiviFieldCard/DiviFieldCard';
import ScoreButtonDropdown from '../../../customs/scoreButtonDropdown/ScoreButtonDropdown';
import {
  getLimbMovementTuplesFor,
  LIMB_MOVEMENTS_ELEMENT_KEY,
  LimbMovementsRecord,
  LimbMovementType,
} from '../../../../models/genericElements/limbMovements';
import { CardId } from '../../../../models/diviCard';
import { NodeType } from '../../../../backendModels/report.model';
import { ReportsAPIContext } from '../../../../provider/ReportsAPIProvider';
import { InputState } from '../../../../backendModels/general.model';
import { Draft } from 'immer';
import { isGenericRecordDeletable } from '../../../../models/generic';
import { useFreshCallback } from '../../../../utils/hooks';

interface LimbMovementsProps {
  nodeType: NodeType;
}

const limbMovementsItems = getLimbMovementTuplesFor([0, 1, 2]);

export default function LimbMovements({ nodeType }: LimbMovementsProps) {
  const { findRecordOrDefault, adaptRecord } = useContext(ReportsAPIContext);
  const record = findRecordOrDefault('generic', nodeType, LIMB_MOVEMENTS_ELEMENT_KEY) as LimbMovementsRecord;

  const selectedLimbMovementReArm =
    record.inputState === InputState.ENTERED && record.values?.extremitaetenbewegungReArm?.number
      ? record.values?.extremitaetenbewegungReArm?.number - 1
      : null;
  const selectedLimbMovementLiArm =
    record.inputState === InputState.ENTERED && record.values?.extremitaetenbewegungLiArm?.number
      ? record.values?.extremitaetenbewegungLiArm?.number - 1
      : null;
  const selectedLimbMovementReBein =
    record.inputState === InputState.ENTERED && record.values?.extremitaetenbewegungReBein?.number
      ? record.values?.extremitaetenbewegungReBein?.number - 1
      : null;
  const selectedLimbMovementLiBein =
    record.inputState === InputState.ENTERED && record.values?.extremitaetenbewegungLiBein?.number
      ? record.values?.extremitaetenbewegungLiBein?.number - 1
      : null;

  const handleLimbMovementsChange = (limb: LimbMovementType, index: number | null) => {
    adaptRecord(
      'generic',
      nodeType,
      (draft: Draft<LimbMovementsRecord>, deleteRecord) => {
        if (index != null) {
          draft.inputState = InputState.ENTERED;
          draft.values = {
            ...draft.values,
            [limb.valueOf()]: {
              fieldType: 'numeric',
              number: index + 1,
            },
          };
        } else {
          delete draft.values?.[limb.valueOf()];
          if (isGenericRecordDeletable(draft)) {
            deleteRecord();
          }
        }
      },
      LIMB_MOVEMENTS_ELEMENT_KEY
    );
  };

  const setToNormal = useFreshCallback(() => {
    // set all entries at once to circumvent sync error
    adaptRecord(
      'generic',
      nodeType,
      (draft: Draft<LimbMovementsRecord>) => {
        draft.inputState = InputState.ENTERED;
        draft.values = {
          ...draft.values,
          extremitaetenbewegungReArm: {
            fieldType: 'numeric',
            number: 1,
          },
          extremitaetenbewegungLiArm: {
            fieldType: 'numeric',
            number: 1,
          },
          extremitaetenbewegungReBein: {
            fieldType: 'numeric',
            number: 1,
          },
          extremitaetenbewegungLiBein: {
            fieldType: 'numeric',
            number: 1,
          },
        };
      },
      LIMB_MOVEMENTS_ELEMENT_KEY
    );
  });

  return (
    <DiviFieldCard
      cardType={CardId.LimbMovements}
      onNormalStateButtonClicked={setToNormal}
      nodeType={nodeType}
      recordType='generic'
      elementKey={LIMB_MOVEMENTS_ELEMENT_KEY}>
      <div className='score-button-dropdown-list no-margin-top'>
        <ScoreButtonDropdown
          title={'Arm rechts'}
          listOfOptions={limbMovementsItems}
          selectedValue={selectedLimbMovementReArm}
          onChange={(value) => handleLimbMovementsChange(LimbMovementType.extremitaetenbewegungReArm, value)}
        />
      </div>
      <div className='score-button-dropdown-list'>
        <ScoreButtonDropdown
          title={'Arm links'}
          listOfOptions={limbMovementsItems}
          selectedValue={selectedLimbMovementLiArm}
          onChange={(value) => handleLimbMovementsChange(LimbMovementType.extremitaetenbewegungLiArm, value)}
        />
      </div>
      <div className='score-button-dropdown-list'>
        <ScoreButtonDropdown
          title={'Bein rechts'}
          listOfOptions={limbMovementsItems}
          selectedValue={selectedLimbMovementReBein}
          onChange={(value) => handleLimbMovementsChange(LimbMovementType.extremitaetenbewegungReBein, value)}
        />
      </div>
      <div className='score-button-dropdown-list'>
        <ScoreButtonDropdown
          title={'Bein links'}
          listOfOptions={limbMovementsItems}
          selectedValue={selectedLimbMovementLiBein}
          onChange={(value) => handleLimbMovementsChange(LimbMovementType.extremitaetenbewegungLiBein, value)}
        />
      </div>
    </DiviFieldCard>
  );
}
